import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// The LEAF, not ./FilterContext — that file imports this slice, and reading
// its defaults from here mid-initialisation is a blank-screen ReferenceError.
import {
  defaultFilterState,
  type FilterState,
} from "@/features/leads/components/leadFilter/context/filterState";

interface RouteFilterState extends FilterState {
  appliedFilters: Record<string, any>;
  /**
   * The clock this route's Apply diffs `dateType` against, and Clear returns
   * to. ABSENT on the pipeline stage routes: their default is the dynamic one
   * (`statusChangedAt`), which the server itself re-applies on a `?stageId=`
   * request, so it is never sent. A route SEEDED with a dateType
   * (`initializeFilterRoute({ dateType })` — the intake stage route, the
   * report builder's two panes) diffs against THAT clock instead: picking
   * "Status Changed At" there is a real choice, applied, chipped and
   * serialised with its companions (`stageChangedToStageId`, `entryLevel`,
   * `intakeStageId`) exactly as the leads page does. Before this the
   * builder's panes never sent their clock — the serializer drops the
   * companions without it — and "moved into Offer Letter Issued yesterday"
   * ran on Created At: 0 against the stage route's 9 (owner, 2026-09-24).
   */
  clockDefault?: string;
  /**
   * REPORT PANES ONLY: this side's dates were CLEARED — no window of its own,
   * which is not the same as "All". On the leads page the two are one state
   * (no range), so it is NOT a FilterState key: it never reaches
   * appliedFilters, a chip or a request. The builder reads it off the route
   * (`mergeLeadParams` opts.noDates): with Break down by cleared, a range on
   * Against windows the whole report instead of comparing "All time vs
   * Yesterday" (owner, 2026-10-06). Set by the pane's "Clear dates", dropped
   * by any date pick ("All" included) and by Clear all.
   */
  noDates?: boolean;
}

const dynamicDefaultFilterState: FilterState = {
  ...defaultFilterState,
  dateType: "statusChangedAt",
};

const defaultRouteState: RouteFilterState = {
  ...dynamicDefaultFilterState,
  appliedFilters: {},
};

/** A route back to its defaults, on the clock it was seeded with. */
const freshRoute = (prev?: RouteFilterState): RouteFilterState =>
  prev?.clockDefault
    ? { ...defaultRouteState, dateType: prev.clockDefault, clockDefault: prev.clockDefault }
    : { ...defaultRouteState };

export type DynamicFilterState = Record<string | number, RouteFilterState>;

const initialState: DynamicFilterState = {};

const dynamicFilterSlice = createSlice({
  name: "dynamicFilter",
  initialState,
  reducers: {
    /**
     * Seeds a route's filter state ONCE. `dateType` overrides the dynamic
     * default (`statusChangedAt`) at creation time — the intake stage's route
     * passes `createdAt`, because leads are CREATED into that stage and the
     * server coerces stage-entry semantics away there. Seeding only on creation
     * is deliberate: re-running it must never clobber a choice the user has
     * since made on this route.
     *
     * `seed` carries a FULL filter set, which is how a Favorite View opens on
     * its saved filters. It may include `appliedFilters`, because a view is
     * applied the moment it opens — it does not open unapplied and wait for a
     * click on Apply.
     */
    initializeFilterRoute: (
      state,
      action: PayloadAction<
        | string
        | number
        | {
            routeId: string | number;
            dateType?: string;
            seed?: Partial<RouteFilterState>;
          }
      >,
    ) => {
      const payload = action.payload;
      const routeId =
        typeof payload === "object" ? payload.routeId : payload;
      const seedDateType =
        typeof payload === "object" ? payload.dateType : undefined;
      const seed = typeof payload === "object" ? payload.seed : undefined;
      if (!state[routeId]) {
        state[routeId] = {
          ...defaultRouteState,
          ...(seedDateType ? { dateType: seedDateType, clockDefault: seedDateType } : {}),
          ...(seed ?? {}),
        };
      } else if (seedDateType && state[routeId].clockDefault === undefined) {
        // The baseline is not a user choice, so a route that already exists
        // (auto-created by an earlier write) still learns its clock; the
        // user's draft is left exactly as it is.
        state[routeId].clockDefault = seedDateType;
      }
    },

    /**
     * Overwrites a route's filter state outright — the "Reset to saved" path on
     * a Favorite View, and the re-seed when a view's definition is updated.
     * Unlike `initializeFilterRoute` this DOES clobber, which is the point.
     */
    reseedFilterRoute: (
      state,
      action: PayloadAction<{
        routeId: string | number;
        seed: Partial<RouteFilterState>;
      }>,
    ) => {
      const { routeId, seed } = action.payload;
      state[routeId] = { ...freshRoute(state[routeId]), ...seed };
    },

    setDynamicFilterField: (
      state,
      action: PayloadAction<{
        routeId: string | number;
        key: keyof FilterState;
        value: any;
      }>
    ) => {
      const { routeId, key, value } = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      (state[routeId] as any)[key] = value;
    },

    /** Merges a single key/value into nicheFilters for a given route */
    setDynamicNicheFilterField: (
      state,
      action: PayloadAction<{
        routeId: string | number;
        key: string;
        value: string | number | boolean;
      }>
    ) => {
      const { routeId, key, value } = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      state[routeId].nicheFilters = {
        ...state[routeId].nicheFilters,
        [key]: value,
      };
    },

    setDynamicSearch: (
      state,
      action: PayloadAction<{ routeId: string | number; search: string }>
    ) => {
      const { routeId, search } = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      state[routeId].search = search;
    },

    applyDynamicFilters: (state, action: PayloadAction<string | number>) => {
      const routeId = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      const route = state[routeId] as Record<string, any>;
      // Call window: one picked date = that single day (same normalisation as
      // the main leadFilterSlice.applyFilters).
      if (route.callStartDate && !route.callEndDate)
        route.callEndDate = route.callStartDate;
      else if (route.callEndDate && !route.callStartDate)
        route.callStartDate = route.callEndDate;
      if (!route.callStartDate && !route.callEndDate) {
        route.callStartTime = "";
        route.callEndTime = "";
      }
      const applied: Record<string, any> = {};
      // `dateType` diffs against the route's own clock (see clockDefault).
      const baseline: FilterState = route.clockDefault
        ? { ...dynamicDefaultFilterState, dateType: route.clockDefault }
        : dynamicDefaultFilterState;
      for (const [key, defaultVal] of Object.entries(baseline)) {
        if (key === "search") continue;
        if (
          key === "nicheFilters" ||
          key === "customFieldFilters" ||
          key === "customFieldPairs"
        ) {
          const obj = (route as any)[key] as Record<string, unknown>;
          if (obj && Object.keys(obj).length > 0) {
            applied[key] = obj;
          }
        } else {
          const currentVal = (route as any)[key];
          if (currentVal !== defaultVal) {
            applied[key] = currentVal;
          }
        }
      }
      route.appliedFilters = applied;
    },

    clearDynamicFilters: (state, action: PayloadAction<string | number>) => {
      state[action.payload] = freshRoute(state[action.payload]);
    },

    resetDynamicFilters: (state, action: PayloadAction<string | number>) => {
      state[action.payload] = freshRoute(state[action.payload]);
    },
  },
});

export const {
  initializeFilterRoute,
  reseedFilterRoute,
  setDynamicFilterField,
  setDynamicNicheFilterField,
  setDynamicSearch,
  applyDynamicFilters,
  clearDynamicFilters,
  resetDynamicFilters,
} = dynamicFilterSlice.actions;

export const selectDynamicFilters = (
  state: any,
  routeId: string | number
): RouteFilterState => state.dynamicFilter[routeId] || defaultRouteState;

export default dynamicFilterSlice.reducer;
